<script>
    import $locale from '../../global/locale';
    import ibTableBasic from './ib-table-basic.vue';
    import ibTableCol from './ib-table-col.vue';
    import iconHamburger from '../../icon/basic/hamburger.svg';
    import iconMinusFilled from '../../icon/basic/minus_filled.svg';

    const indexOf = Array.prototype.indexOf;

    function mid(el) {
        const rect = el.getBoundingClientRect();
        return rect.top + rect.bottom >> 1;
    }

    /**
     * Extension of [ib-table-basic](#/components/table/ib-table-basic) with deletable and draggable rows. Row
     * visibility and order is determined by `value`; therefore, column sorting does not work with this table.
     */
    export default {
        name: 'ib-table-edit',

        mixins: [
            ibTableBasic
        ],

        props: {
            /**
             * This component accepts data as either an array, like `ib-table-basic`, or an object
             * with keys equal to row IDs.
             */
            data: {
                type: [Array,Object],
                default: () => {}
            },
            /**
             * An array of currently selected table rows by id, in selection order.
             */
            value: {
                type: Array,
                default: undefined
            },
            /**
             * DOM `name` attribute; used as property in enclosing `ib-form`.
             */
            name: String,
            /**
             * If `true`, allow removal of rows.
             */
            removable: Boolean,
            /**
             * If `true`, require two clicks to remove a row by exposing a secondary button.
             */
            confirm: Boolean,
            /**
             * If `true`, allow reordering of rows via drag.
             */
            movable: Boolean,
            /**
             * If `true`, or for an array containing a specific row's id, disable changes.
             */
            disabled: [Boolean,Array],
            /**
             * If `true`, the form entry state will be invalid unless at least one row is selected.
             */
            required: Boolean
        },

        data() {
            return {
                ivalue: [],
                dragIndex: -1,
                shiftIndex: -1,
                shiftHeight: 0,
                moved: false
            };
        },

        computed: {
            indexed() {
                const data = this.data;
                if (Array.isArray(data)) {
                    return data.reduce((o,data,index) => {
                        const id = this.getid(data, index);
                        o[id] = { data, index, id };
                        return o;
                    }, {});
                } else {
                    return Object.keys(data).reduce((o,id) => {
                        o[id] = { data: data[id], id };
                        return o;
                    }, {});
                }
            },

            sorted() {
                // If `value` is not specified, show all entries
                if (this.value === void 0 && Array.isArray(this.data)) {
                    return this.data.map((data, index) => ({ data, index, id: this.getid(data, index) }));
                } else {
                    const indexed = this.indexed;
                    return this.ivalue.filter(id => indexed.hasOwnProperty(id)).map(id => indexed[id]);
                }
            },

            handleEnd() {
                return false;
            },

            selfSlotEnd() {
                return this.removable && this.confirm;
            }
        },

        watch: {
            value: {
                handler(value) {
                    if (Array.isArray(value)) {
                        this.ivalue = value.slice();
                    } else {
                        this.ivalue = [];
                    }
                },
                immediate: true
            }
        },

        updated() {
            if (this.dtr) {
                this.dragGeometry();
            }
        },

        beforeDestroy() {
            this.clean();
        },

        methods: {
            renderBefore(h) {
                return this.control ? [] : [
                    h('ib-form-entry', {
                        'attrs': { 'name': this.name || '_tb' + this._uid, 'value': this.selected }
                    })
                ];
            },

            renderHeadBefore(h) {
                return this.removable ? [
                    h(ibTableCol, {
                        'attrs': {
                            sticky: 'start',
                            width: '2.75em',
                            force: true
                        }
                    })] : [];
            },

            renderHeadAfter(h) {
                return this.movable ? [
                    h(ibTableCol, {
                        'attrs': {
                            sticky: 'end',
                            width: '32px',
                            force: true
                        }
                    })] : [];
            },

            renderSlotEnd(h, data, disabled) {
                return disabled ? null : [
                    h('ib-button', {
                        'attrs': {
                            'tone': 'error'
                        },
                        'on': {
                            'click': this.remove
                        }
                    }, [
                        this._v($locale.data.Remove)
                    ])
                ];
            },

            renderBody(h) {
                const body = this.renderSlot('body');
                const disabled = this._disabled;
                const renderRow = this.renderRow;

                return h('tbody', {
                        'ref': 'body',
                        'staticClass': '_tbb _tbe',
                        'class': {
                            '_tb-moved': this.moved
                        }
                    },
                    this.sorted.length
                        ? this._l(this.sorted, (row,index) => {
                            const disabled = this.getdisabled(row.id);
                            return renderRow(h, row.id, [
                                ...this.renderBodyStart(h),
                                this.removable ? h('td', {
                                    'class': '_tb-remove',
                                    'on': disabled ? null : {
                                        click: this.confirm ? this.slotEndClick : this.remove
                                    },
                                    'domProps': {
                                        innerHTML: iconMinusFilled
                                    }
                                }) : void 0,
                                body(row.data),
                                this.movable ? h('td', {
                                    'class': '_tb-move fs8',
                                    'on': disabled ? null : {
                                        mousedown: this.dragStart,
                                        touchstart: this.dragStart
                                    },
                                    'domProps': {
                                        innerHTML: disabled ? null : iconHamburger
                                    }
                                }) : void 0,
                                ...this.renderBodyEnd(h, row.id, row.data, disabled)
                            ], {
                                '_tbe-dd': index >= this.shiftIndex && index < this.di,
                                '_tbe-du': index < this.shiftIndex && index > this.di,
                                '_tde': disabled
                            });
                        })
                        : this.renderEmpty(h));
            },

            dragStart(e) {
                if (!e.button) {
                    const tr = e.target.closest('tr');
                    const index = indexOf.call(this.$refs.body.children, tr);
                    if (index >= 0) {
                        const id = this.sorted[index].id;
                        if (id !== this.ulid) {
                            this.di =
                            this.shiftIndex = index;
                            this.did = this.sorted[index].id;
                            this.dtr = tr;
                            this.dtr.classList.add('_drag');
                            this.dy = 0;
                            this.dragGeometry();
                            this._dragcapture(tr, e);
                        }
                    }
                    e.preventDefault();
                }
            },

            dragGeometry() {
                const trr = this.dtr.getBoundingClientRect();
                const tbr = this.$refs.body.getBoundingClientRect();
                this.dmin = tbr.top - trr.top;
                this.dmax = tbr.bottom - trr.bottom;
            },

            _dragmove(x, y) {
                const dy = Math.max(this.dmin, Math.min(this.dmax, y));
                const transform = 'translateY(' + dy + 'px)';
                const td = this.dtr.children;
                for (let i = 0; i < td.length; i++) {
                    td[i].style.transform = transform;
                }
                const tr = this.$refs.body.children;
                const sorted = this.sorted;
                const disabled = this._disabled;
                const rect = this.dtr.getBoundingClientRect();
                let shiftIndex = this.shiftIndex;
                if (y < this.dy) {
                    let i = shiftIndex;
                    while (i > 0 && mid(tr[i - 1]) - 4 > rect.top + dy) {
                        i--;
                        if (!disabled || !disabled.includes(sorted[i].id)) {
                            shiftIndex = i;
                        }
                    }
                } else {
                    let i = shiftIndex;
                    while (i < tr.length && mid(tr[i]) + 4 < rect.bottom + dy) {
                        i++;
                        if (!disabled || !disabled.includes(sorted[i-1].id)) {
                            shiftIndex = i;
                        }
                    }
                }

                // Generate CSS classes to offset rows during drag
                const shiftHeight = rect.height;
                if (this.shiftHeight !== shiftHeight) {
                    this.shiftHeight = shiftHeight;
                    let style = this.style;
                    if (style) {
                        while (style.sheet.cssRules.length) {
                            style.sheet.deleteRule(style.sheet.cssRules.length - 1);
                        }
                    } else {
                        style = this.style = document.createElement('style');
                        style.appendChild(document.createTextNode(''));
                        document.head.appendChild(style);
                    }
                    const id = '#' + this._id;
                    style.sheet.insertRule(id + ' ._tbe-dd>*{transform:translateY(' + shiftHeight + 'px)', 0);
                    style.sheet.insertRule(id + ' ._tbe-du>*{transform:translateY(-' + shiftHeight + 'px)', 1);
                }
                this.shiftIndex = shiftIndex;
                this.dy = y;
            },

            _dragup(x, y) {
                const di = this.di;
                let shiftIndex = this.shiftIndex;
                if (shiftIndex > di) {
                    shiftIndex--;
                }
                if (di != shiftIndex) {
                    this.ivalue.splice(shiftIndex, 0, this.ivalue.splice(di, 1)[0]);
                    this.$emit('input', this.ivalue);
                    this.moved = true;
                    setTimeout(() => {
                        this.moved = false;
                    }, 120);
                }
                const td = this.dtr.children;
                for (let i = 0; i < td.length; i++) {
                    td[i].style.transform = '';
                }

                this.dtr.classList.remove('_drag');
                this.dtr = null;
                this.did = null;
                this.shiftIndex = -1;
                this.shiftHeight = null;
                this.di = -1;
                this.clean();
            },

            clean() {
                if (this.style) {
                    document.head.removeChild(this.style);
                    this.style = null;
                }
            },

            remove(e) {
                const tr = e.target.closest('tr');
                const index = indexOf.call(this.$refs.body.children, tr);
                if (index >= 0) {
                    this.$emit('remove', this.getid(this.sorted[index], index));
                    if (this.value !== void 0) {
                        this.ivalue.splice(index, 1);
                        this.$emit('input', this.ivalue);
                    }
                }
            },

            before() {
                return [{ width: 0 }].concat(this.removable ? [{ width: '2.75em', sticky: 'start', force: true }] : []);
            },

            after() {
                return (
                    (this.movable ? [{ width: '32px', sticky: 'end', force: true }] : []).concat(
                    (this.selfSlotEnd ? [{ width: '0', sticky: 'end', force: true }] : []).concat([{ width: 0 }])));
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._tbe {
        td {
            transition: box-shadow .6s, opacity .6s, transform .3s, z-index 0s linear .6s;
        }
    }
    td._tb-remove {
        padding: 0;
        vertical-align: middle;
        color: @fg-remove;
        cursor: pointer;
    }
    td._tb-move {
        padding: 0;
        vertical-align: middle;
        color: mix(@fg, @bg, 30%);
        cursor: grab;
    }
    tr._tde {
        color: mix(@fg, @bg, 50%);
        ._tb-move {
            cursor: default;
        }
        ._tb-remove {
            color: mix(@fg, @bg, 15%);
            cursor: default;
        }
    }
    tr._drag {
        > td {
            opacity: .75;
            z-index: 5 !important;
            transition: box-shadow .6s, opacity .6s;
            .elevation(3, 0, .5);
        }
        ._tb-move {
            color: mix(@fg, @bg, 50%);
        }
    }
    ._tb-moved {
        > tr,
        > tr > td {
            transition: none;
        }
    }
</style>

<ib-example id="widgets" name="Movable/Removable Widgets">
    <template lang="pug">
        div
            ib-table-edit(:data="widgets", v-model="selected", emit="id", movable, removable, confirm)
                ib-table-col Name
                template(v-slot:body="widget")
                    td(v-text="widget.name")

            .inset-16.fs7.monospace(id="value", v-text="selected.join(', ')")
    </template>
    <script>
        {
            data() {
                return {
                    widgets: [
                        { id: 'mktupdate', name: 'Market Update' },
                        { id: 'positions', name: 'Portfolio Positions' },
                        { id: 'manage', name: 'Manage Your Account' },
                        { id: 'statements', name: 'Activity Statements' },
                        { id: 'ibkrnews', name: 'Recent News from IBKR' },
                        { id: 'portfolionews', name: 'Portfolio News' },
                        { id: 'forum', name: 'Recent Posts on IBKR Forum' },
                        { id: 'watchlist', name: 'Watchlist' },
                        { id: 'trades', name: 'Trades' },
                        { id: 'transactions', name: 'Transactions' },
                        { id: 'portfolioanalyst', name: 'PortfolioAnalyst' }
                    ],
                    selected: [
                        'mktupdate',
                        'positions',
                        'manage',
                        'statements',
                        'ibkrnews',
                        'portfolionews',
                        'forum',
                        'watchlist',
                        'trades',
                        'transactions',
                        'portfolioanalyst'
                    ]
                };
            }
        }
    </script>
</ib-example>

<ib-example id="object" name="Data as Object, No Delete Confirmation">
    <template lang="pug">
        div
            ib-table-edit(:data="widgets", v-model="selected", movable, removable)
                ib-table-col Name
                template(v-slot:body="widget")
                    td(v-text="widget")

            .inset-16.fs7.monospace(id="value", v-text="selected.join(', ')")
    </template>
    <script>
        {
            data() {
                return {
                    widgets: {
                        mktupdate: 'Market Update',
                        positions: 'Portfolio Positions',
                        manage: 'Manage Your Account',
                        statements: 'Activity Statements',
                        ibkrnews: 'Recent News from IBKR',
                        portfolionews: 'Portfolio News',
                        forum: 'Recent Posts on IBKR Forum',
                        watchlist: 'Watchlist',
                        trades: 'Trades',
                        transactions: 'Transactions',
                        portfolioanalyst: 'PortfolioAnalyst'
                    },
                    selected: [
                        'mktupdate',
                        'positions',
                        'manage',
                        'statements',
                        'ibkrnews',
                        'portfolionews',
                        'forum',
                        'watchlist',
                        'trades',
                        'transactions',
                        'portfolioanalyst'
                    ]
                };
            }
        }
    </script>
</ib-example>
